import { useCallback, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TextInput, DatePicker } from '@/components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import { getCommercialTerms } from '@/services/commercialTermService';
import { updateEmployee } from '@/services/employeeService';
import { useDepartmentsForCascade } from '@features/departments/hooks/useDepartmentsForCascade';
import { useJobTitlesForCascade } from '@features/job-titles/hooks/useJobTitlesForCascade';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { useTranslation } from '@/hooks/useTranslation';
import { useCountries } from '@/hooks/useCountries';
import { toApiDateTime } from '@utils/dateHelpers';
import type { EmployeeDto, UpdateEmployeePayload } from '@/types/employee';
import styles from './EmployeeEditForm.module.scss';

// ============================================
// TYPES
// ============================================

interface EmployeeEditFormValues {
  firstName: string;
  lastName: string;
  preferredName: string;
  workEmail: string;
  // Form state stores ids only — Selects bind to value/onChange and the rest
  // of the DTO (name, code) is resolved server-side on the next GET.
  departmentId: string;
  jobTitleId: string;
  employmentTypeId: string;
  contractType: string;
  startDate: string;
  grossSalary: string;
  salaryCurrency: string;
  salaryFrequency: string;
  probationMonths: string;
  weeklyWorkingHours: string;
}

interface EmployeeEditFormProps {
  employeeData: EmployeeDto;
  onSaved: () => void;
  formId?: string;
  onPendingChange?: (pending: boolean) => void;
}

function useContractTypeOptions() {
  const { t } = useTranslation();
  return useMemo(() => [
    { label: t('K_OPTION_PERMANENT', 'Permanent'), value: 'Permanent' },
    { label: t('K_OPTION_FIXED_TERM', 'Fixed Term'), value: 'Fixed Term' },
    { label: t('K_OPTION_CASUAL', 'Casual'), value: 'Casual' },
  ], [t]);
}

function useSalaryFrequencyOptions() {
  const { t } = useTranslation();
  return useMemo(() => [
    { label: t('K_OPTION_MONTHLY', 'Monthly'), value: 'MONTHLY' },
    { label: t('K_OPTION_BI_WEEKLY', 'Bi-Weekly'), value: 'BI_WEEKLY' },
    { label: t('K_OPTION_WEEKLY', 'Weekly'), value: 'WEEKLY' },
  ], [t]);
}

const CURRENCY_OPTIONS = [
  { label: 'USD', value: 'USD' },
  { label: 'EUR', value: 'EUR' },
  { label: 'GBP', value: 'GBP' },
  { label: 'KES', value: 'KES' },
  { label: 'NGN', value: 'NGN' },
  { label: 'ZAR', value: 'ZAR' },
  { label: 'INR', value: 'INR' },
];

// ============================================
// COMPONENT
// ============================================

function EmployeeEditForm({ employeeData, onSaved, formId, onPendingChange }: EmployeeEditFormProps) {
  const { t } = useTranslation();
  const contractTypeOptions = useContractTypeOptions();
  const salaryFrequencyOptions = useSalaryFrequencyOptions();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const { countries } = useCountries();

  const countryName = useMemo(() => {
    const country = countries.find((c) => c.id === employeeData.countryId);
    return country?.name ?? employeeData.countryId;
  }, [countries, employeeData.countryId]);

  const { data: commercialTermsData } = useQuery({
    queryKey: ['commercialTerms', 'TENANT', employeeData.tenantId],
    queryFn: () => getCommercialTerms('TENANT', employeeData.tenantId, { size: 100 }),
    enabled: !!employeeData.tenantId,
  });

  const employmentTypeOptions = useMemo(() => {
    const terms = (commercialTermsData?.content ?? []).filter(
      (t) => t.countryId === employeeData.countryId,
    );
    const unique = new Map<string, string>();
    for (const term of terms) {
      if (term.employmentType && !unique.has(term.employmentType.id)) {
        unique.set(term.employmentType.id, term.employmentType.name);
      }
    }
    return Array.from(unique, ([value, label]) => ({ label, value }));
  }, [commercialTermsData, employeeData.countryId]);

  const formValues = useMemo<EmployeeEditFormValues>(() => ({
    firstName: employeeData.firstName ?? '',
    lastName: employeeData.lastName ?? '',
    preferredName: employeeData.preferredName ?? '',
    workEmail: employeeData.workEmail ?? '',
    departmentId: employeeData.department?.id ?? '',
    jobTitleId: employeeData.jobTitle?.id ?? '',
    employmentTypeId: employeeData.employmentTypeId ?? '',
    contractType: employeeData.contractType ?? '',
    startDate: employeeData.startDate ? employeeData.startDate.split('T')[0] ?? '' : '',
    grossSalary: String(employeeData.grossSalary ?? ''),
    salaryCurrency: employeeData.salaryCurrency ?? '',
    salaryFrequency: employeeData.salaryFrequency ?? '',
    probationMonths: employeeData.probationMonths != null ? String(employeeData.probationMonths) : '',
    weeklyWorkingHours: employeeData.weeklyWorkingHours != null ? String(employeeData.weeklyWorkingHours) : '',
  }), [employeeData]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EmployeeEditFormValues>({
    values: formValues,
    resetOptions: { keepDirtyValues: true },
    mode: 'onChange',
  });

  // Cascade — Department drives Job Title options. Switching department clears
  // the currently-selected job title so a stale id never round-trips to the BE.
  //
  // Orphan handling for edit mode: if the employee was created with a department
  // or job title that's since been deactivated (or, for JT, re-associated away
  // from this employee's department), the active cascade won't return it. We
  // inject the stored value as a fallback option so the Select renders the
  // current label instead of silently showing blank. The id round-trips
  // unchanged unless the user explicitly picks something else, preserving data.
  const selectedDepartmentId = watch('departmentId');
  const { departments, isLoading: depsLoading } = useDepartmentsForCascade();
  const { jobTitles, isLoading: jtLoading } = useJobTitlesForCascade(
    selectedDepartmentId || undefined,
  );
  const departmentOptions = useMemo(() => {
    const opts = departments.map((d) => ({ label: d.name, value: d.id }));
    const storedDept = employeeData.department;
    if (storedDept && !opts.some((o) => o.value === storedDept.id)) {
      opts.unshift({ label: storedDept.name, value: storedDept.id });
    }
    return opts;
  }, [departments, employeeData.department]);
  const jobTitleOptions = useMemo(() => {
    const opts = jobTitles.map((j) => ({ label: j.name, value: j.id }));
    const storedJt = employeeData.jobTitle;
    // Only inject if it belongs under the currently-selected department (else
    // the user has already switched away and the orphan would mislead).
    if (
      storedJt
      && employeeData.department?.id === selectedDepartmentId
      && !opts.some((o) => o.value === storedJt.id)
    ) {
      opts.unshift({ label: storedJt.name, value: storedJt.id });
    }
    return opts;
  }, [jobTitles, employeeData.jobTitle, employeeData.department, selectedDepartmentId]);

  const mutation = useMutation({
    mutationFn: (payload: UpdateEmployeePayload) => updateEmployee(employeeData.id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employee', employeeData.id] });
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      void queryClient.invalidateQueries({ queryKey: ['employeeCounts'] });
      void queryClient.invalidateQueries({ queryKey: ['allEmployeeCounts'] });
      onSaved();
    },
    onError: (error) => {
      // NOTE: validateJobTitleAndDepartment failures (INVALID_JOB_TITLE /
      // INVALID_DEPARTMENT / DEPARTMENT_NOT_IN_JOB_TITLE) all collapse to
      // WP_BAD_REQUEST (4000) under the uniform envelope — the backend uses the
      // legacy AppException ctor which discards the discriminating errorCode, so
      // per-field inline errors can no longer be wired. Generic toast for now.
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  const onSubmit = useCallback(
    (data: EmployeeEditFormValues) => {
      const payload: UpdateEmployeePayload = {
        firstName: data.firstName,
        lastName: data.lastName,
        preferredName: data.preferredName || undefined,
        workEmail: data.workEmail,
        jobTitle: { id: data.jobTitleId },
        department: { id: data.departmentId },
        employmentTypeId: data.employmentTypeId,
        contractType: data.contractType,
        startDate: toApiDateTime(data.startDate) ?? data.startDate,
        grossSalary: Number(data.grossSalary),
        salaryCurrency: data.salaryCurrency,
        salaryFrequency: data.salaryFrequency,
        probationMonths: data.probationMonths ? Number(data.probationMonths) : undefined,
        weeklyWorkingHours: data.weeklyWorkingHours ? Number(data.weeklyWorkingHours) : undefined,
      };
      mutation.mutate(payload);
    },
    [mutation],
  );

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.formGrid}>
        <TextInput
          label={t('K_LABEL_FIRST_NAME', 'First Name')}
          isRequired
          error={errors.firstName?.message}
          {...register('firstName', { required: t('K_VALIDATION_FIRST_NAME_REQUIRED', 'First name is required') })}
        />
        <TextInput
          label={t('K_LABEL_LAST_NAME', 'Last Name')}
          isRequired
          error={errors.lastName?.message}
          {...register('lastName', { required: t('K_VALIDATION_LAST_NAME_REQUIRED', 'Last name is required') })}
        />
        <TextInput
          label={t('K_LABEL_PREFERRED_NAME', 'Preferred Name')}
          {...register('preferredName')}
        />
        <TextInput
          label={t('K_LABEL_WORK_EMAIL', 'Work Email')}
          isRequired
          error={errors.workEmail?.message}
          {...register('workEmail', { required: t('K_VALIDATION_WORK_EMAIL_REQUIRED', 'Work email is required') })}
        />
        <TextInput
          label={t('K_LABEL_COUNTRY', 'Country')}
          value={countryName}
          disabled
        />
        <Controller
          name="departmentId"
          control={control}
          rules={{ required: t('K_VALIDATION_DEPARTMENT_REQUIRED', 'Department is required') }}
          render={({ field }) => (
            <Select
              label={t('K_LABEL_DEPARTMENT', 'Department')}
              isRequired
              placeholder={t('K_PLACEHOLDER_SELECT_DEPARTMENT', 'Select a department')}
              options={departmentOptions}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                // Clear stale JT when department flips — the new department's
                // cascade may not contain the previously-selected JT.
                if (value !== field.value) {
                  setValue('jobTitleId', '', { shouldValidate: true, shouldDirty: true });
                }
              }}
              loading={depsLoading}
              error={errors.departmentId?.message}
            />
          )}
        />
        <Controller
          name="jobTitleId"
          control={control}
          rules={{ required: t('K_VALIDATION_JOB_TITLE_REQUIRED', 'Job title is required') }}
          render={({ field }) => (
            <Select
              label={t('K_LABEL_JOB_TITLE', 'Job Title')}
              isRequired
              placeholder={
                !selectedDepartmentId
                  ? t('K_PLACEHOLDER_SELECT_DEPARTMENT', 'Select a department')
                  : t('K_PLACEHOLDER_SELECT_JOB_TITLE', 'Select a job title')
              }
              options={jobTitleOptions}
              value={field.value}
              onChange={field.onChange}
              disabled={!selectedDepartmentId}
              loading={jtLoading}
              error={errors.jobTitleId?.message}
            />
          )}
        />
        <Controller
          name="employmentTypeId"
          control={control}
          rules={{ required: t('K_VALIDATION_EMPLOYMENT_TYPE_REQUIRED', 'Employment type is required') }}
          render={({ field }) => (
            <Select
              label={t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type')}
              isRequired
              options={employmentTypeOptions}
              value={field.value}
              onChange={field.onChange}
              error={errors.employmentTypeId?.message}
            />
          )}
        />
        <Controller
          name="contractType"
          control={control}
          rules={{ required: t('K_VALIDATION_CONTRACT_TYPE_REQUIRED', 'Contract type is required') }}
          render={({ field }) => (
            <Select
              label={t('K_LABEL_CONTRACT_TYPE', 'Contract Type')}
              isRequired
              options={contractTypeOptions}
              value={field.value}
              onChange={field.onChange}
              error={errors.contractType?.message}
            />
          )}
        />
        <Controller
          name="startDate"
          control={control}
          rules={{ required: t('K_VALIDATION_START_DATE_REQUIRED', 'Start date is required') }}
          render={({ field }) => (
            <DatePicker
              label={t('K_LABEL_START_DATE', 'Start Date')}
              isRequired
              value={field.value}
              onChange={field.onChange}
              error={errors.startDate?.message}
              locale="en-GB"
              fullWidth
            />
          )}
        />
        <TextInput
          label={t('K_LABEL_GROSS_SALARY', 'Gross Salary')}
          isRequired
          type="number"
          error={errors.grossSalary?.message}
          {...register('grossSalary', { required: t('K_VALIDATION_GROSS_SALARY_REQUIRED', 'Gross salary is required') })}
        />
        <Controller
          name="salaryCurrency"
          control={control}
          rules={{ required: t('K_VALIDATION_CURRENCY_REQUIRED', 'Currency is required') }}
          render={({ field }) => (
            <Select
              label={t('K_LABEL_SALARY_CURRENCY', 'Salary Currency')}
              isRequired
              options={CURRENCY_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              error={errors.salaryCurrency?.message}
              searchable
            />
          )}
        />
        <Controller
          name="salaryFrequency"
          control={control}
          rules={{ required: t('K_VALIDATION_SALARY_FREQUENCY_REQUIRED', 'Salary frequency is required') }}
          render={({ field }) => (
            <Select
              label={t('K_LABEL_SALARY_FREQUENCY', 'Salary Frequency')}
              isRequired
              options={salaryFrequencyOptions}
              value={field.value}
              onChange={field.onChange}
              error={errors.salaryFrequency?.message}
            />
          )}
        />
        <TextInput
          label={t('K_LABEL_PROBATION_PERIOD', 'Probation Period (Months)')}
          type="number"
          placeholder={t('K_PLACEHOLDER_PROBATION_MONTHS', 'e.g. 3')}
          {...register('probationMonths')}
        />
        <TextInput
          label={t('K_LABEL_WEEKLY_WORKING_HOURS', 'Weekly Working Hours')}
          type="number"
          placeholder={t('K_PLACEHOLDER_WEEKLY_HOURS', 'e.g. 40')}
          {...register('weeklyWorkingHours')}
        />
      </div>
    </form>
  );
}

export default EmployeeEditForm;
